import { useAuth, API } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, 
  Users, 
  Clock, 
  ListChecks, 
  CalendarOff, 
  Calendar,
  LogOut,
  Menu,
  X,
  CreditCard,
  BarChart3,
  Copy,
  Building2,
  Upload,
  Eye,
  Bell,
  Settings as SettingsIcon,
  Moon,
  Sun,
  FileText,
  AlertTriangle,
  Mail,
  ClipboardList,
  GripVertical,
  EyeOff
} from "lucide-react";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import Logo from "./Logo";
import EmailVerificationBanner from "./EmailVerificationBanner";
import CustomizeSidebarDialog from "./CustomizeSidebarDialog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/teams", label: "Teams", icon: Building2 },
  { path: "/employees", label: "Employees", icon: Users },
  { path: "/shifts", label: "Shift Types", icon: Clock },
  { path: "/rules", label: "Rules", icon: ListChecks },
  { path: "/sop-rules", label: "SOP Rules", icon: FileText },
  { path: "/time-off", label: "Time Off", icon: CalendarOff },
  { path: "/operations", label: "Operations", icon: ClipboardList },
  { path: "/rosters", label: "Rosters", icon: Calendar },
  { path: "/import", label: "Import", icon: Upload },
  { path: "/templates", label: "Templates", icon: Copy },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/settings", label: "Settings", icon: SettingsIcon },
  { path: "/subscription", label: "Subscription", icon: CreditCard },
  { path: "/contact", label: "Contact us", icon: Mail },
];

// Mirror of backend subscription check — read-only client-side hint for UI.
const subscriptionActive = (user) => {
  const status = user?.subscription_status || "trial";
  if (status === "active") return true;
  if (status === "trial") {
    const ts = user?.trial_start;
    if (!ts) return true;
    try {
      const start = new Date(ts);
      const days = (Date.now() - start.getTime()) / (1000 * 60 * 60 * 24);
      return days < 14;
    } catch { return true; }
  }
  return false;
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [sidebarLayout, setSidebarLayout] = useState({ order: [], hidden: [] });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("theme") === "dark";
    } catch {
      return false;
    }
  });

  // Apply dark mode class to <html>
  const applyTheme = (isDark) => {
    const root = document.documentElement;
    if (isDark) root.classList.add("dark");
    else root.classList.remove("dark");
  };

  // Sync theme on mount and when toggled
  if (typeof window !== "undefined") {
    applyTheme(darkMode);
  }

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    try {
      localStorage.setItem("theme", next ? "dark" : "light");
    } catch { /* ignore */ }
    applyTheme(next);
  };

  // Fetch pending staff requests
  useState(() => {
    const fetchNotifs = async () => {
      try {
        const res = await axios.get(`${API}/notifications/pending-count`, { withCredentials: true });
        setPendingCount(res.data.pending_count || 0);
      } catch { /* ignore */ }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  // Load persisted sidebar layout once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/user/sidebar-layout`, { withCredentials: true });
        if (!cancelled) setSidebarLayout({
          order: res.data?.order || [],
          hidden: res.data?.hidden || [],
        });
      } catch { /* ignore — use defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Compute the displayed nav: respect saved order, append any new defaults at the end,
  // and filter out hidden items.
  const orderedNav = (() => {
    const byPath = Object.fromEntries(navItems.map((i) => [i.path, i]));
    const out = [];
    const seen = new Set();
    for (const p of sidebarLayout.order) {
      if (byPath[p]) { out.push(byPath[p]); seen.add(p); }
    }
    for (const item of navItems) {
      if (!seen.has(item.path)) out.push(item);
    }
    return out.filter((i) => !sidebarLayout.hidden.includes(i.path));
  })();

  const persistLayout = async (next) => {
    setSidebarLayout(next);
    try {
      await axios.put(`${API}/user/sidebar-layout`, next, { withCredentials: true });
    } catch {
      toast.error("Couldn't save sidebar order");
    }
  };

  const fetchPendingDetails = async () => {
    try {
      const res = await axios.get(`${API}/notifications/pending`, { withCredentials: true });
      setPendingRequests(res.data);
    } catch { /* ignore */ }
  };

  const handleOpenNotifs = () => {
    fetchPendingDetails();
    setNotifOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 flex transition-colors">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-800
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center justify-between">
              <Logo size={28} />
              <div className="flex items-center gap-1">
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors"
                  data-testid="dark-mode-toggle"
                  title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {darkMode ? (
                    <Sun className="w-5 h-5 text-amber-400" />
                  ) : (
                    <Moon className="w-5 h-5 text-zinc-500" />
                  )}
                </button>
                {/* Notification Bell */}
                <Popover open={notifOpen} onOpenChange={setNotifOpen}>
                  <PopoverTrigger asChild>
                    <button
                      onClick={handleOpenNotifs}
                      className="relative p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors"
                      data-testid="notification-bell"
                    >
                      <Bell className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                      {pendingCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                          data-testid="notification-badge">
                          {pendingCount > 9 ? "9+" : pendingCount}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0 dark:bg-zinc-900 dark:border-zinc-800" align="start">
                    <div className="p-3 border-b border-zinc-200 dark:border-zinc-800">
                      <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Staff Leave Requests</h3>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">{pendingCount} pending</p>
                    </div>
                    {pendingRequests.length === 0 ? (
                      <div className="p-4 text-center text-sm text-zinc-500 dark:text-zinc-400">No pending requests</div>
                    ) : (
                      <div className="max-h-[300px] overflow-y-auto divide-y divide-zinc-100 dark:divide-zinc-800">
                        {pendingRequests.map((req) => (
                          <div key={req.request_id} className="p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{req.employee_name}</span>
                              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium capitalize">{req.status}</span>
                            </div>
                            <p className="text-xs text-zinc-600 dark:text-zinc-400">
                              {(req.leave_type || "").replace(/_/g, " ")} — {req.start_date} to {req.end_date}
                            </p>
                            {req.reason && <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">{req.reason}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="p-2 border-t border-zinc-200 dark:border-zinc-800">
                      <button
                        onClick={() => { setNotifOpen(false); navigate("/time-off"); }}
                        className="w-full text-center text-xs font-medium text-[#002FA7] dark:text-blue-400 hover:underline py-1"
                        data-testid="view-all-requests-btn"
                      >
                        View all in Time Off
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                  data-testid="close-sidebar-btn"
                >
                  <X className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
                </button>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            {orderedNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-6 py-3 text-sm font-medium
                    transition-colors text-left
                    ${isActive 
                      ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 border-l-2 border-[#002FA7] dark:border-blue-400' 
                      : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100 border-l-2 border-transparent'
                    }
                  `}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </button>
              );
            })}
            <button
              onClick={() => setCustomizeOpen(true)}
              className="w-full flex items-center gap-3 px-6 py-3 text-xs font-medium text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 border-l-2 border-transparent"
              data-testid="customize-sidebar-btn"
            >
              <GripVertical className="w-4 h-4" />
              Customise menu
            </button>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-10 h-10 border border-zinc-200 dark:border-zinc-700">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 text-sm font-medium">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
          </button>
          <Logo size={24} />
          <div className="w-9" /> {/* Spacer */}
        </header>

        {/* Email-verification banner (non-blocking, dismissible) */}
        <EmailVerificationBanner user={user} />

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          {/* Impersonation Banner */}
          {user?._impersonating && (
            <div className="mb-6 bg-amber-50 border-2 border-amber-300 p-3 flex items-center justify-between rounded" data-testid="impersonation-banner">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-medium text-amber-800">
                  Viewing as <span className="font-bold">{user?.name}</span> ({user?.email})
                </p>
              </div>
              <button
                onClick={() => {
                  sessionStorage.removeItem("impersonate_token");
                  window.close();
                }}
                className="text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                data-testid="stop-impersonation-btn"
              >
                Stop Impersonating
              </button>
            </div>
          )}
          {/* Subscription gate banner */}
          {user && !subscriptionActive(user) && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-800 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded" data-testid="subscription-gate-banner">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Your {user.subscription_status === "active" ? "subscription" : "trial"} has ended — you're in <span className="font-bold">read-only</span> mode. Generating, editing, and creating are disabled until you upgrade.
                </p>
              </div>
              <button
                onClick={() => navigate("/subscription")}
                className="text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded whitespace-nowrap"
                data-testid="banner-upgrade-btn"
              >
                Upgrade now
              </button>
            </div>
          )}
          {children}
        </main>
      </div>

      <CustomizeSidebarDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        allItems={navItems}
        layout={sidebarLayout}
        onSave={persistLayout}
      />
    </div>
  );
};

export default Layout;
